import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import {
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const Invoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("due_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [actionLoading, setActionLoading] = useState(null);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);

      // Prepare filters
      const filters = {};

      // If user is not super admin, filter by their club
      if (user.role !== "super_admin" && user.club_id) {
        filters.clubId = user.club_id;
      }

      const response = await apiService.getAllInvoices(filters);
      setInvoices(response.invoices || response);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [user.role, user.club_id]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      setActionLoading(invoiceId);
      // Get the PDF blob directly from the API service
      const pdfBlob = await apiService.downloadInvoice(invoiceId);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download invoice");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePay = async (invoiceId) => {};

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "overdue":
        return "destructive";
      case "draft":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Filter and sort invoices
  const filteredAndSortedInvoices = invoices
    .filter((invoice) => {
      const matchesSearch =
        invoice.invoice_number
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        invoice.clubs?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "due_date":
          aValue = new Date(a.due_date);
          bValue = new Date(b.due_date);
          break;
        case "invoice_date":
          aValue = new Date(a.invoice_date);
          bValue = new Date(b.invoice_date);
          break;
        case "amount":
          aValue = parseFloat(a.total_amount);
          bValue = parseFloat(b.total_amount);
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = a.invoice_number;
          bValue = b.invoice_number;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600">View and manage your invoices</p>
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by invoice number or club name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </Select>
            </div>

            {/* Sort Options */}
            <div className="w-full sm:w-48">
              <Select
                value={`${sortBy}-${sortOrder}`}
                onValueChange={(value) => {
                  const [field, order] = value.split("-");
                  setSortBy(field);
                  setSortOrder(order);
                }}
              >
                <option value="due_date-desc">Due Date (Newest)</option>
                <option value="due_date-asc">Due Date (Oldest)</option>
                <option value="invoice_date-desc">Invoice Date (Newest)</option>
                <option value="invoice_date-asc">Invoice Date (Oldest)</option>
                <option value="amount-desc">Amount (Highest)</option>
                <option value="amount-asc">Amount (Lowest)</option>
                <option value="status-asc">Status (A-Z)</option>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Count */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Showing {filteredAndSortedInvoices.length} of {invoices.length}{" "}
          invoices
        </p>
      </div>

      {/* Invoices List */}
      {filteredAndSortedInvoices.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CurrencyDollarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No invoices found
            </h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search criteria"
                : "You don't have any invoices yet"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAndSortedInvoices.map((invoice) => (
            <Card
              key={invoice.id}
              className="hover:shadow-md transition-shadow"
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Invoice Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {invoice.invoice_number}
                        </h3>
                        {user.role === "super_admin" && (
                          <p className="text-sm text-gray-600">
                            {invoice.clubs?.name}
                          </p>
                        )}
                      </div>
                      <Badge variant={getStatusBadgeVariant(invoice.status)}>
                        {invoice.status?.charAt(0).toUpperCase() +
                          invoice.status?.slice(1)}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="font-semibold text-lg">
                          {formatCurrency(invoice.total_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Invoice Date</p>
                        <p className="font-medium">
                          {formatDate(invoice.invoice_date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Due Date</p>
                        <p className="font-medium">
                          {formatDate(invoice.due_date)}
                        </p>
                      </div>
                    </div>

                    {invoice.billing_period_start &&
                      invoice.billing_period_end && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-500">
                            Billing Period
                          </p>
                          <p className="text-sm font-medium">
                            {formatDate(invoice.billing_period_start)} -{" "}
                            {formatDate(invoice.billing_period_end)}
                          </p>
                        </div>
                      )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handlePay(invoice.id)}
                      loading={actionLoading === invoice.id}
                    >
                      Pay
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleDownloadInvoice(
                          invoice.id,
                          invoice.invoice_number,
                        )
                      }
                      loading={actionLoading === invoice.id}
                      disabled={actionLoading === invoice.id}
                    >
                      <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Invoices;
